import React, {useEffect, useState, useContext} from 'react';
import classnames from 'classnames';
import {Tooltip, Popconfirm, Dropdown, Space} from 'antd';
import {EditOutlined, CheckSquareTwoTone, CloseSquareTwoTone, PlusSquareTwoTone, MinusSquareTwoTone, DeleteOutlined} from '@ant-design/icons';
import {isUndefined, get} from 'lodash';
import {OpenApiContext} from '../openapi-context';


export function Block({className, onCancel, name, onSave, canSave = true, onEdit, children, noEdit, onAdd, onRemove, onRemoveBlock, addOptions, delegatedEditMode, mobile, disableEdit}) {
    const {state, dispatch, SUPPORTED_EDITOR_ROLES} = useContext(OpenApiContext);
    const [blockClasses, setBlockClasses] = useState(classnames('block', className));
    const [editMode, setEditMode] = useState();

    const handleSave = () => {
        try {
            setEditMode(false);
            onEdit && onEdit(false);
            onSave && onSave();
        } catch (err) {
            console.error(err);
        }
    };

    const handleCancel = () => {
        try {
            setEditMode(false);
            onEdit && onEdit(false);
            onCancel && onCancel();
        } catch (err) {
            console.error(err);
        }
    };

    const handleEdit = () => {
        if (mobile || disableEdit) {
            return;
        }
        setEditMode(true);
        onEdit && onEdit(true);
    };

    const handleOnAdd = () => {};

    const handleOnRemove = () => {
        onRemove && onRemove();
    };

    const handleOnRemoveBlock = () => {
        onRemoveBlock && onRemoveBlock();
    };

    const canEdit = () => {
        if (noEdit) {
            return false;
        } else {
            return isUndefined(delegatedEditMode) && SUPPORTED_EDITOR_ROLES.indexOf(get(state, 'role')) !== -1;
        }
    };

    useEffect(() => {
        if (!canEdit()) {
            setBlockClasses(classnames('block', className, 'noEdit'));
        } else if (editMode) {
            setBlockClasses(classnames('block', className));
        } else {
            setBlockClasses(classnames('block', className, 'view'));
        }
        // Only top-level Blocks (the ones whose user clicks the pencil)
        // dispatch global edit-mode. Delegated Blocks (description /
        // footer / etc.) MUST NOT dispatch — their local editMode is
        // a one-way mirror of state.editMode set by effect-2 below, so
        // dispatching from them would close a feedback loop:
        //   effect-1 dispatches activate/terminate from local
        //   global state.editMode flips
        //   effect-2 mirrors new state.editMode into local
        //   ... but since the dispatch already moved state, local now
        //       INVERTS relative to state, and effect-1 fires again
        //       with the opposite verb. Infinite ping-pong.
        // The user-reported "continuous flicker on description after
        // pencil click" was exactly this loop.
        if (isUndefined(delegatedEditMode)) {
            dispatch({type: editMode ? 'activate' : 'terminate', message: name ? `changes to ${name}` : undefined});
        }
    }, [editMode, noEdit, state.role]);

    useEffect(() => {
        if (!isUndefined(delegatedEditMode)) {
            setEditMode(delegatedEditMode);
        }
    }, [delegatedEditMode]);

    const gutterButtonStyle = {fontSize: '16pt'};

    // The gutter chrome (edit pencil / save+cancel / add / remove) used
    // to live in a left Col span=1 column, which reserved ~4% of every
    // Block's width even when invisible. We now float it absolutely in
    // the top-right so the content column gets full width and the
    // chrome reads as a per-block toolbar instead of a permanent rail.
    return (
        <div className={blockClasses}>
            {canEdit() && (
                <div className="gutter gutter-floating">
                    {!editMode && !state.editMode && (
                        <Space>
                            <Tooltip className="gutterButton" placement="left" title={disableEdit ? 'finish renaming first' : 'edit this block'}>
                                <EditOutlined
                                    style={{
                                        ...gutterButtonStyle,
                                        ...(disableEdit ? {color: 'rgba(0,0,0,0.25)', cursor: 'not-allowed'} : {}),
                                    }}
                                    onClick={handleEdit}
                                />
                            </Tooltip>
                            {onRemoveBlock && (
                                <Popconfirm title="Do you really want to remove this block?" okType="default" okText="yes" cancelText="no" onConfirm={handleOnRemoveBlock}>
                                    <DeleteOutlined style={gutterButtonStyle} />
                                </Popconfirm>
                            )}
                        </Space>
                    )}
                    {editMode && (
                        <Space>
                            {canSave ? (
                                <Tooltip className="gutterButton" placement="left" title="save your changes">
                                    <CheckSquareTwoTone style={gutterButtonStyle} onClick={handleSave} twoToneColor="green" />
                                </Tooltip>
                            ) : (
                                <Tooltip className="gutterButton" placement="left" title="cannot save right now. check validation errors">
                                    <CheckSquareTwoTone style={gutterButtonStyle} twoToneColor="gray" />
                                </Tooltip>
                            )}
                            <Popconfirm title="Sure to cancel?" okType="default" okText="yes" cancelText="no" onConfirm={handleCancel}>
                                <span className="gutterButton">
                                    <Tooltip placement="left" title="cancel and revert">
                                        <CloseSquareTwoTone style={gutterButtonStyle} twoToneColor="red" />
                                    </Tooltip>
                                </span>
                            </Popconfirm>
                            {onAdd && (
                                <Tooltip className="gutterButton" placement="left" title="insert block">
                                    <PlusSquareTwoTone style={gutterButtonStyle} onClick={handleOnAdd} twoToneColor="blue" />
                                </Tooltip>
                            )}
                            {addOptions && (
                                <Dropdown menu={addOptions}>
                                    <span className="gutterButton">
                                        <Tooltip placement="left" title="insert block">
                                            <PlusSquareTwoTone style={gutterButtonStyle} onClick={handleOnAdd} twoToneColor="blue" />
                                        </Tooltip>
                                    </span>
                                </Dropdown>
                            )}
                            {onRemove && (
                                <Tooltip className="gutterButton" placement="left" title="remove block">
                                    <MinusSquareTwoTone style={gutterButtonStyle} onClick={handleOnRemove} twoToneColor="orange" />
                                </Tooltip>
                            )}
                        </Space>
                    )}
                </div>
            )}
            <div className="content">
                {children}
            </div>
        </div>
    );
}
